import { CORE_SUB_PAGES } from './coreSubPages'

export { CORE_SUB_PAGES }

/** Phase 1 核心专题页文案（每页独立搜索意图） */
export const CORE_PAGES = {
	features: {
		path: '/features/',
		title: 'GB WhatsApp Pro Features – Privacy, Themes & Chat Tools',
		description:
			'Explore GB WhatsApp Pro features: privacy controls, customization, chat tools, and media sharing. Learn what each option does and where limits apply.',
		keywords: 'gb whatsapp pro features, gb whatsapp pro privacy, gb whatsapp pro themes',
		heroKicker: 'Feature overview',
		h1: 'GB WhatsApp Pro Features',
		heroIntro:
			'GB WhatsApp Pro adds optional controls on top of the familiar WhatsApp-style chat experience. This page explains the feature groups people search for most often, without treating mods as official products.',
		sections: [
			{
				h2: 'Privacy features',
				paragraphs: [
					'Modified clients often advertise extra privacy switches, such as hiding online status, delaying read receipts, or limiting typing indicators. These settings can change how others perceive your activity, but they do not make your account invisible to WhatsApp systems.',
					'If privacy is your main concern, compare what you need against what the official app already offers, and read our <a href="/safety/privacy-risks/">privacy risks page</a> before installing unofficial software.',
				],
				list: [
					'Ghost-style online controls (wording varies by build)',
					'Blue tick and second tick visibility options',
					'Optional anti-delete message viewing when enabled',
				],
				linkCards: [
					{ href: '/features/privacy-features/', label: 'Privacy features guide', desc: 'Deep dive on toggles and limits' },
				],
			},
			{
				h2: 'Privacy vs official WhatsApp',
				paragraphs: [
					'Official WhatsApp already includes last seen, profile photo, about, and read receipt controls for many users. GB WhatsApp Pro may add extra toggles (ghost mode, per-contact rules, anti-delete viewing), but those extras do not remove Meta’s ability to enforce account rules or change server behavior.',
					'If official privacy settings meet your needs, staying on Google Play WhatsApp avoids sideload risk. Choose a mod only when you accept unofficial distribution and possible account restrictions.',
				],
			},
			{
				h2: 'Customization',
				paragraphs: [
					'Theme packs, fonts, and chat wallpapers are a common reason people look for GB WhatsApp Pro. Customization can make daily chats easier to scan, especially if you manage personal and work conversations in one app.',
					'Remember that heavy visual mods may not survive every update. Back up theme-related settings if your build allows export.',
				],
				linkCards: [
					{ href: '/features/customization/', label: 'Customization guide', desc: 'Themes, fonts, wallpapers' },
				],
			},
			{
				h2: 'Customization vs official WhatsApp',
				paragraphs: [
					'Official WhatsApp supports chat wallpapers and light/dark themes tied to system settings. GB WhatsApp Pro often adds third-party theme packs, font engines, and bubble styles that are not available in the Play Store app.',
					'Visual mods can increase APK size and crash rates after updates. Official WhatsApp receives design updates automatically; mod themes may break until the publisher releases a compatible pack.',
				],
			},
			{
				h2: 'Chat features',
				paragraphs: [
					'Scheduling, auto-reply, and chat lock options appear in many community builds. They can help with reminders or busy periods, but automated replies should stay short and honest so contacts know they are not talking to you directly.',
				],
				list: [
					'Message scheduler for delayed sending',
					'Auto-reply templates for unavailable hours',
					'Per-chat lock or app-level lock when supported',
				],
				linkCards: [
					{ href: '/features/chat-features/', label: 'Chat features guide', desc: 'Scheduler, auto-reply, lock' },
				],
			},
			{
				h2: 'Chat tools vs official WhatsApp',
				paragraphs: [
					'Official WhatsApp offers pinned chats, starred messages, and official Chat Lock on supported versions—but not third-party schedulers or mod auto-reply engines. GB WhatsApp Pro fills that gap for users who want automation, with the trade-off of unofficial support.',
					'Business users on WhatsApp Business should compare official catalog, labels, and API policies before relying on mod auto-replies that contacts may misread as personal messages.',
				],
			},
			{
				h2: 'Media sharing',
				paragraphs: [
					'Some versions raise file size limits or add status download helpers. Larger uploads can fail on slow networks, and downloaded status media may involve privacy expectations—only save content you have permission to keep.',
				],
				linkCards: [
					{ href: '/features/media-sharing/', label: 'Media sharing guide', desc: 'File limits and status download' },
				],
			},
			{
				h2: 'Media vs official WhatsApp',
				paragraphs: [
					'Official WhatsApp enforces file size and format limits that change over time. Some GB builds advertise larger uploads or built-in status savers; those features still depend on network quality and may conflict with copyright or privacy expectations when saving others’ status media.',
					'HD photo settings and document sharing on official WhatsApp may suffice if your main goal is quality rather than bypassing limits.',
				],
			},
			{
				h2: 'What GB WhatsApp Pro cannot promise',
				paragraphs: [
					'No mod replaces official WhatsApp support, Play Store update channels, or guaranteed account stability. Feature names change between releases; verify options inside your installed version instead of assuming every guide applies verbatim.',
				],
			},
			{
				h2: 'How menus differ between builds',
				paragraphs: [
					'Publishers move options between GB Settings, Privacy, and Universal tabs when they rebrand a release. If a guide mentions a toggle you cannot find, search settings for keywords (theme, ghost, scheduler) before assuming the feature was removed.',
					'After every APK update, spend one minute re-opening privacy and chat tool menus. Updates that fix crashes sometimes reset custom themes or disable experimental toggles until you turn them on again.',
				],
			},
			{
				h2: 'Step-by-step feature articles',
				linkCards: [
					{ href: '/schedule-messages-gb-whatsapp/', label: 'Schedule messages', desc: 'Queue sends for later' },
					{ href: '/lock-individual-chats-gb-whatsapp/', label: 'Lock chats', desc: 'PIN or biometric gates' },
					{ href: '/hide-blue-ticks-gb-whatsapp/', label: 'Hide blue ticks', desc: 'Read receipt controls' },
					{ href: '/use-gb-whatsapp-dnd-mode/', label: 'DND mode', desc: 'Mod-specific quiet modes' },
					{ href: '/download-gb-whatsapp-status-videos/', label: 'Save status videos', desc: 'When your build allows it' },
				],
			},
		],
	},
	installation: {
		path: '/installation/',
		title: 'How to Install GB WhatsApp Pro on Android',
		description:
			'Step-by-step GB WhatsApp Pro installation for Android: backups, APK permissions, verification, and fixes when setup fails.',
		keywords: 'install gb whatsapp pro, gb whatsapp pro apk install, gb whatsapp pro android',
		heroKicker: 'Installation guide',
		h1: 'Install GB WhatsApp Pro on Android',
		heroIntro:
			'Installing an APK manually is straightforward when you prepare backups and use a trusted file. Follow these steps before you change anything on a phone that already has WhatsApp data you care about.',
		sections: [
			{
				h2: 'Before you install',
				paragraphs: [
					'Charge your phone or connect power, connect to stable Wi‑Fi, and confirm you have free storage for the APK plus temporary install space. Export or back up chats using the method your current app supports.',
				],
				list: [
					'Back up chats to local storage or cloud if available',
					'Note your current version number for rollback planning',
					'Download the APK only from a source you trust',
				],
			},
			{
				h2: 'Install the APK',
				steps: [
					'Open the downloaded APK from your notification or Downloads folder.',
					'If Android asks to allow installs from that browser or file app, enable the setting for this install only.',
					'Review permissions on the install screen and continue if you accept them.',
					'Wait for installation to finish, then open GB WhatsApp Pro.',
					'Verify your phone number and restore a backup when prompted.',
				],
			},
			{
				h2: 'Install alongside or replace an existing app',
				paragraphs: [
					'Some builds use a different package name so they can run next to official WhatsApp; others replace the original install. Do not assume dual-app mode works on every device—check the APK documentation and Android version.',
				],
			},
			{
				h2: 'Related installation guides',
				linkCards: [
					{ href: '/installation/android/', label: 'Android install', desc: 'Step-by-step on phone' },
					{ href: '/installation/backup-chats/', label: 'Back up chats', desc: 'Before any APK change' },
					{ href: '/installation/restore-chats/', label: 'Restore chats', desc: 'After verification' },
					{ href: '/installation/without-uninstalling/', label: 'Without uninstalling', desc: 'Dual app scenarios' },
				],
			},
			{
				h2: 'When installation fails',
				paragraphs: [
					'See <a href="/help/not-installing/">GB WhatsApp Pro not installing</a> for parse errors, version conflicts, and Play Protect blocks. Re-download the file if the APK might be incomplete.',
				],
			},
			{
				h2: 'Install vs official WhatsApp from Play Store',
				paragraphs: [
					'Official WhatsApp installs through Google Play with automatic updates and Play Protect scanning. GB WhatsApp Pro requires manual APK installs, permission to unknown sources, and your own judgment about file integrity.',
					'You cannot merge Play Store update channels with a sideloaded mod. Plan manual updates from <a href="/download/">download</a> and read <a href="/update/">update guide</a> before each upgrade.',
				],
			},
			{
				h2: 'After installation checks',
				paragraphs: [
					'Once verification succeeds, confirm that recent chats and groups appear as expected before deleting old backups. Send a test message to a trusted contact and ask them to reply while you watch delivery ticks.',
				],
				list: [
					'Open Android Settings → Apps → GB WhatsApp Pro → Notifications and enable all channels',
					'Note the version string under in-app Settings for future updates',
					'Run a fresh backup if the first launch restored older data',
					'Review permissions you granted during install and remove any that seem unrelated',
				],
			},
			{
				h2: 'Common installation mistakes',
				list: [
					'Installing over official WhatsApp without reading dual-app vs replace documentation',
					'Skipping backup because the APK “worked before” on another phone',
					'Using a mirror link with a different file size than <a href="/download/">download</a>',
					'Uninstalling when Android shows Update instead of a fresh install prompt',
				],
			},
		],
	},
	update: {
		path: '/update/',
		title: 'How to Update GB WhatsApp Pro Without Losing Chats',
		description:
			'Update GB WhatsApp Pro manually: check your version, download a new APK, back up chats, and recover when an update fails.',
		keywords: 'update gb whatsapp pro, gb whatsapp pro latest version, gb whatsapp pro apk update',
		heroKicker: 'Update guide',
		h1: 'Update GB WhatsApp Pro',
		heroIntro:
			'Because GB WhatsApp Pro is not updated through Google Play, you need a repeatable manual process. Backups matter more here than on official apps.',
		sections: [
			{
				h2: 'Why updates matter',
				paragraphs: [
					'Newer builds may fix crashes, adjust compatibility with recent Android versions, or change feature menus. Staying on a very old APK can also make it harder to restore backups created on newer releases.',
				],
			},
			{
				h2: 'Check your current version',
				paragraphs: [
					'Open Settings → App info (wording varies) and note the version string. Compare it with the version listed on our <a href="/download/">download page</a> before you download again.',
				],
			},
			{
				h2: 'Recommended update flow',
				steps: [
					'Back up chats locally or to cloud storage.',
					'Download the latest APK from a trusted source.',
					'Install over the existing app when Android allows an upgrade install.',
					'Open the app and confirm chats and settings loaded correctly.',
					'If install is blocked, read <a href="/update/update-failed/">update failed help</a> before uninstalling.',
				],
			},
			{
				h2: 'If you must uninstall first',
				paragraphs: [
					'Uninstalling can delete local data. Confirm backups open on another device or storage path before you remove the old APK. Never uninstall because of a single error message without checking storage and file integrity first.',
				],
			},
			{
				h2: 'Update sub-guides',
				linkCards: [
					{ href: '/update/without-losing-chats/', label: 'Update without losing chats', desc: 'Ordered backup flow' },
					{ href: '/update/check-version/', label: 'Check version', desc: 'Match build to download page' },
					{ href: '/update/update-failed/', label: 'Update failed', desc: 'When install over old APK fails' },
				],
			},
			{
				h2: 'When to pause an update',
				paragraphs: [
					'If your current build is stable and you are traveling or relying on one verification SMS, delay updating until you have Wi‑Fi and time to restore backups. Jumping to every leaked APK increases signature mismatch risk.',
					'Read community notes for your specific version before downgrading. Older APKs may not open backups created on newer releases.',
				],
			},
			{
				h2: 'Updates vs official WhatsApp',
				paragraphs: [
					'Official WhatsApp updates silently through Play Store when you opt into automatic updates. GB WhatsApp Pro updates only when you download and install a new APK—there is no Google-managed rollback if a build misbehaves.',
					'If you need security patches quickly, official WhatsApp is the lower-friction path. Mod users should follow publisher release notes and our verified download details instead of chasing unverified “latest” mirrors.',
				],
			},
			{
				h2: 'Update-related blog articles',
				linkCards: [
					{ href: '/gb-whatsapp-update-failed/', label: 'Update failed', desc: 'Blog walkthrough' },
					{ href: '/gb-whatsapp-expired-version/', label: 'Expired version message', desc: 'What it means' },
					{ href: '/gb-whatsapp-not-working-after-update/', label: 'Not working after update', desc: 'Post-upgrade fixes' },
					{ href: '/gb-whatsapp-android-compatibility/', label: 'Android compatibility', desc: 'OS vs APK match' },
				],
			},
		],
	},
	safety: {
		path: '/safety/',
		title: 'GB WhatsApp Pro Safety, Risks & Account Restrictions',
		description:
			'Balanced safety information for GB WhatsApp Pro: unofficial distribution, privacy, fake APKs, and account restriction risks.',
		keywords: 'gb whatsapp pro safe, gb whatsapp pro ban risk, gb whatsapp pro security',
		heroKicker: 'Safety information',
		h1: 'Is GB WhatsApp Pro Safe to Use?',
		heroIntro:
			'Short answer: no modified WhatsApp client is officially endorsed, and none can be guaranteed safe. This page explains the trade-offs so you can decide with clear expectations.',
		sections: [
			{
				h2: 'Unofficial software by definition',
				paragraphs: [
					'GB WhatsApp Pro is distributed as an APK outside Google Play. That means you rely on the publisher’s integrity, not on Meta’s review process. Updates, privacy practices, and data handling can differ from official WhatsApp.',
				],
			},
			{
				h2: 'Account restriction risk',
				paragraphs: [
					'WhatsApp may limit or ban accounts that use third-party clients. Marketing terms like “anti-ban” describe feature intent, not a contractual guarantee. If account stability matters for work or family contact, weigh that risk carefully.',
				],
				linkCards: [
					{ href: '/safety/account-ban-risk/', label: 'Account ban risk', desc: 'Signals, myths, and safer habits' },
				],
			},
			{
				h2: 'APK source integrity',
				paragraphs: [
					'Fake or repackaged APK files can include unwanted code. Compare file size with published details when available, avoid “too good to be true” download mirrors, and scan with Android security tools you already trust.',
				],
				linkCards: [
					{ href: '/safety/malware-and-fake-apks/', label: 'Malware & fake APKs', desc: 'Red flags before you tap Install' },
					{ href: '/safety/privacy-risks/', label: 'Privacy risks', desc: 'What mods can and cannot hide' },
				],
			},
			{
				h2: 'When official WhatsApp is the better fit',
				paragraphs: [
					'If you need predictable support, multi-device policies, or business verification, stay on the official app. GB WhatsApp Pro information on this site is for users who already accept mod-specific risks.',
				],
			},
			{
				h2: 'If you already saw a warning or ban message',
				paragraphs: [
					'Stop requesting verification codes in a loop—read the on-screen text first. Screenshot timers, support links, and whether chats still send. Back up while the app still opens, then decide between waiting out a temporary limit or migrating to official WhatsApp.',
					'GBWhatPro.org cannot lift restrictions on your number. Use our guides to protect data and understand policy context; appeals and account recovery go through official WhatsApp channels when available.',
				],
				linkCards: [
					{ href: '/why-is-gb-whatsapp-account-banned/', label: 'Why accounts get restricted', desc: 'Temporary vs permanent signals' },
					{ href: '/switch-from-gb-whatsapp-to-official-whatsapp/', label: 'Switch to official WhatsApp', desc: 'Backup limits explained' },
				],
			},
			{
				h2: 'Pre-install risk checklist',
				list: [
					'You downloaded the APK from a source you can compare with our verified file details',
					'You understand WhatsApp may restrict accounts using unofficial clients',
					'You backed up chats and know how to restore them',
					'You are not installing solely because of “anti-ban” marketing language',
					'Sensitive work or health conversations have a fallback on official WhatsApp if needed',
				],
			},
			{
				h2: 'In-depth safety articles',
				linkCards: [
					{ href: '/is-gb-whatsapp-safe/', label: 'Is GB WhatsApp safe?', desc: '2026 risk overview' },
					{ href: '/will-gb-whatsapp-get-you-banned/', label: 'Ban risk explained', desc: 'Policy context' },
					{ href: '/does-gb-whatsapp-anti-ban-work/', label: 'Anti-ban claims', desc: 'What mods cannot promise' },
					{ href: '/gb-whatsapp-says-use-official-whatsapp/', label: 'Official app warning', desc: 'When WhatsApp blocks mods' },
				],
			},
		],
	},
	help: {
		path: '/help/',
		title: 'GB WhatsApp Pro Help Center – Troubleshooting Guides',
		description:
			'Help center for GB WhatsApp Pro: installation, crashes, messages, notifications, storage, and verification issues.',
		keywords: 'gb whatsapp pro help, gb whatsapp pro not working, gb whatsapp pro troubleshooting',
		heroKicker: 'Help center',
		h1: 'GB WhatsApp Pro Help Center',
		heroIntro:
			'Pick the symptom that matches your phone. Each linked guide focuses on one problem so you can fix issues in order instead of guessing.',
		sections: [
			{
				h2: 'Start here',
				paragraphs: [
					'Most problems fall into a few buckets: the APK never installed, the app opens then closes, messages stall, or media will not download. Try the matching article before resetting your phone.',
				],
				linkCards: [
					{ href: '/help/not-installing/', label: 'Not installing', desc: 'Parse errors and blocked installs' },
					{ href: '/help/not-opening/', label: 'Not opening', desc: 'Stuck splash or instant close' },
					{ href: '/help/keeps-crashing/', label: 'Keeps crashing', desc: 'After update or on launch' },
					{ href: '/help/messages-not-sending/', label: 'Messages not sending', desc: 'Network and app permissions' },
					{ href: '/help/messages-not-receiving/', label: 'Messages not receiving', desc: 'Sync and background limits' },
					{ href: '/help/photos-videos-not-downloading/', label: 'Media not downloading', desc: 'Storage and connection checks' },
					{ href: '/help/notifications-not-working/', label: 'Notifications off', desc: 'Android notification channels' },
					{ href: '/help/verification-code-not-received/', label: 'No SMS code', desc: 'Verification troubleshooting' },
					{ href: '/help/storage-problems/', label: 'Storage problems', desc: 'Free space and cache cleanup' },
				],
			},
			{
				h2: 'Still stuck?',
				paragraphs: [
					'Document your Android version, app version, and the exact error text. Compare with our <a href="/update/">update guide</a> if the issue began right after installing a new APK. For safety context, see <a href="/safety/">safety information</a>.',
				],
			},
			{
				h2: 'How help articles relate to the blog',
				paragraphs: [
					'Help pages on this site follow a fixed troubleshooting order: describe the symptom, list likely causes, checks, fixes, and next steps. Blog posts go deeper on one topic—ban policy, backup theory, or a single feature—and are grouped under <a href="/blog/">Blog</a> categories.',
					'Start with help when something broke today. Read blog articles when you are researching risk before install or comparing GB WhatsApp Pro with official WhatsApp.',
				],
			},
			{
				h2: 'Suggested paths',
				list: [
					'New install failing → <a href="/help/not-installing/">not installing</a> then <a href="/installation/android/">Android install</a>',
					'Updated yesterday and crashes → <a href="/help/keeps-crashing/">keeps crashing</a> and <a href="/update/update-failed/">update failed</a>',
					'Account warning or ban message → <a href="/safety/account-ban-risk/">ban risk</a> and <a href="/will-gb-whatsapp-get-you-banned/">ban explainer</a>',
				],
			},
			{
				h2: 'Long-form troubleshooting on the blog',
				paragraphs: [
					'When a help page is not enough, these articles walk through one problem end to end with FAQ sections at the bottom.',
				],
				linkCards: [
					{ href: '/gb-whatsapp-verification-code-not-received/', label: 'Verification code not received', desc: 'SMS and dual-SIM checks' },
					{ href: '/gb-whatsapp-keeps-crashing/', label: 'Keeps crashing', desc: 'Storage and compatibility' },
					{ href: '/gb-whatsapp-backup-not-working/', label: 'Backup not working', desc: 'Local vs cloud saves' },
					{ href: '/gb-whatsapp-update-failed/', label: 'Update failed', desc: 'Signature and storage errors' },
				],
			},
		],
	},
}
